import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot field
  });

  const [status, setStatus] = useState({
    type: '', // 'success', 'error', 'loading'
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!formData.name || !formData.phone || !formData.email || !formData.description) {
      setStatus({
        type: 'error',
        message: 'Venligst udfyld alle påkrævede felter.'
      });
      return;
    }

    setStatus({ type: 'loading', message: 'Sender besked...' });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          description: formData.description,
          website_url: formData.website_url // Honeypot
        })
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setStatus({
          type: 'success',
          message: 'Mange tak for din henvendelse! Vi vender tilbage til dig hurtigst muligt.'
        });
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: ''
        });
      } else {
        setStatus({
          type: 'error',
          message: result.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Netværksfejl. Kontroller din internetforbindelse og prøv igen.'
      });
    }
  };

  return (
    <div className="py-20 bg-black animate-fade-in">
      <Helmet>
        <title>Contact – HEINE DESIGN</title>
        <meta name="description" content="Kontakt Heine Design for priser, specialmål eller yderligere information om vores håndlavede danske designmøbler." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="space-y-4 mb-16 text-center">
          <span className="text-xs tracking-[0.25em] uppercase text-brand-oak font-semibold block">
            Kom i kontakt
          </span>
          <h1 className="text-3xl md:text-4xl font-light tracking-tight text-white uppercase">
            Contact
          </h1>
          <div className="h-[1px] w-20 bg-brand-oak mx-auto" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left: Contact Form (lg:col-span-7) */}
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-2">
              <h2 className="text-lg font-light tracking-widest text-brand-dark uppercase">
                Send en besked
              </h2>
              <p className="text-xs text-brand-charcoal/70">
                Udfyld formularen nedenfor, så vender vi tilbage til dig inden for 24 timer.
              </p>
            </div>

            {status.message && (
              <div
                className={`p-4 rounded-none text-xs ${
                  status.type === 'success'
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                    : status.type === 'error'
                    ? 'bg-rose-50 text-rose-800 border border-rose-200'
                    : 'bg-brand-light text-brand-charcoal border border-brand-gray/40'
                }`}
              >
                {status.type === 'loading' && <i className="fa-solid fa-spinner animate-spin mr-2" />}
                {status.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot field (absolutely hidden from humans) */}
              <div className="sr-only absolute -top-9999 -left-9999">
                <label htmlFor="website_url">Do not fill this field</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Name */}
                <div className="space-y-2">
                  <label htmlFor="name" className="text-[10px] tracking-widest uppercase text-brand-charcoal font-semibold block">
                    Navn / Name <span className="text-brand-oak">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-brand-light border border-brand-gray rounded-none px-4 py-3 text-xs text-white focus:outline-none focus:border-brand-oak transition-colors duration-300"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <label htmlFor="phone" className="text-[10px] tracking-widest uppercase text-brand-charcoal font-semibold block">
                    Telefonnummer / Phone <span className="text-brand-oak">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-brand-light border border-brand-gray rounded-none px-4 py-3 text-xs text-white focus:outline-none focus:border-brand-oak transition-colors duration-300"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label htmlFor="email" className="text-[10px] tracking-widest uppercase text-brand-charcoal font-semibold block">
                  E-mailadresse / Email <span className="text-brand-oak">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-brand-light border border-brand-gray rounded-none px-4 py-3 text-xs text-white focus:outline-none focus:border-brand-oak transition-colors duration-300"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label htmlFor="description" className="text-[10px] tracking-widest uppercase text-brand-charcoal font-semibold block">
                  Beskrivelse / Message <span className="text-brand-oak">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows="6"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full bg-brand-light border border-brand-gray rounded-none px-4 py-3 text-xs text-white focus:outline-none focus:border-brand-oak transition-colors duration-300 resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={status.type === 'loading'}
                className="px-8 py-4 bg-brand-red text-white text-xs tracking-[0.2em] uppercase hover:bg-brand-red-dark transition-colors duration-300 rounded-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send besked
              </button>
            </form>
          </div>

          {/* Right: Contact Details (lg:col-span-5) */}
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-brand-light border border-brand-gray rounded-none p-8 space-y-8">
              <div className="space-y-4">
                <h3 className="text-xs tracking-[0.25em] uppercase text-brand-oak font-semibold">
                  Kontaktoplysninger
                </h3>
                <div className="h-[1px] w-12 bg-brand-oak" />
              </div>

              <ul className="space-y-4 text-xs text-brand-charcoal">
                <li className="flex items-start gap-4">
                  <i className="fa-solid fa-location-dot text-brand-oak text-base mt-0.5 w-5" />
                  <div className="space-y-1">
                    <p className="font-semibold text-white">Heine Design</p>
                    <p className="font-light">Klarkærvej 12</p>
                    <p className="font-light">4600 Køge</p>
                    <p className="font-light">Denmark</p>
                  </div>
                </li>
                <li className="flex items-center gap-4">
                  <i className="fa-solid fa-phone text-brand-oak text-base w-5" />
                  <div className="space-y-1">
                    <p className="font-semibold text-white">Mobil / Cellphone</p>
                    <a href="tel:+4522443585" className="font-light hover:text-brand-oak transition-colors">
                      +45 22 44 35 85
                    </a>
                  </div>
                </li>
                <li className="flex items-center gap-4">
                  <i className="fa-solid fa-envelope text-brand-oak text-base w-5" />
                  <div className="space-y-1">
                    <p className="font-semibold text-white">E-mail</p>
                    <a href="mailto:info@heinedesign.com" className="font-light hover:text-brand-oak transition-colors">
                      info@heinedesign.com
                    </a>
                  </div>
                </li>
              </ul>

              <div className="border-t border-brand-gray pt-6 space-y-4">
                <h4 className="text-[10px] tracking-[0.2em] uppercase text-white font-semibold">
                  Bankforbindelse
                </h4>
                <ul className="space-y-2 text-xs text-brand-charcoal/80 font-light">
                  <li className="flex justify-between">
                    <span>Bank:</span>
                    <span>Vendsyssel Sparekasse</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Konto:</span>
                    <span>9070-1628052901</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Swift:</span>
                    <span>VRAADK21</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Iban:</span>
                    <span>DK6590701628052901</span>
                  </li>
                  <li className="flex justify-between">
                    <span>CVR Nr.:</span>
                    <span>Dk 20 43 06 99</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
